import { useCallback, useEffect, useRef } from "react"
import { navigate } from "gatsby"
import type { DomainCheck } from "@onestore/api/domainSearch"
import type { ButtonAtmVariant } from "@onestore/hel/dist/components/atoms/ButtonAtm"
import ButtonAtm from "@onestore/hel/dist/components/atoms/ButtonAtm"
import LoaderAtm from "@onestore/hel/dist/components/atoms/LoaderAtm"
import useBreakpoint from "@onestore/hel/dist/hooks/useBreakpoint"
import type Theme from "@onestore/hel/dist/typings/theme"
import _t from "@core/i18n"
import useButtonLoadingState from "@gatsby-plugin-domain-search/hooks/useButtonLoadingState"
import useDomainActionsDispatch from "@gatsby-plugin-domain-search/hooks/useDomainActionsDispatch"
import useDomainHookUrl from "@gatsby-plugin-domain-search/hooks/useDomainHookUrl"
import CartAdd from "@onestore-icons/cart-add.svg"
import Tick from "@onestore-icons/tick.svg"
import { setAddCartSource } from "~/lib/basket"
import isEmpty from "~/lib/isEmpty"
import url from "~/lib/url"
import { useAppSelector } from "~/store/hooks"
import type { AppState } from "~/store/reducer"

type DomainAddButtonProps = {
  domain: DomainCheck.Result
  color: Theme.Color
  variant?: ButtonAtmVariant
  isWider?: boolean
  eventSource?: string
  onNextButtonClick?: { (): void }
  hasOverriddenSuccessButtonColor?: boolean
  buttonDataTestId?: string
  onDomainSelected?: (domainResult: DomainCheck.Result) => void
  selectedFqdns?: string[]
}

export default function DomainAddButton({
  domain,
  color,
  variant,
  isWider = false,
  eventSource,
  onNextButtonClick,
  hasOverriddenSuccessButtonColor,
  buttonDataTestId,
  onDomainSelected,
  selectedFqdns,
}: DomainAddButtonProps) {
  const successButtonRef = useRef<HTMLButtonElement>(null)
  const isSmallOnly = useBreakpoint("small", "medium")
  const isMediumUp = useBreakpoint("medium")
  const { fqdn } = domain
  const hasIcon = isSmallOnly || false
  const buttonId = `search-${domain.plan_id}`
  const isLoading = useButtonLoadingState(buttonId)
  const isDomainInBasket = useAppSelector((state: AppState) =>
    state.basket.domains.includes(fqdn)
  )
  const { forDomainButtons } = useDomainHookUrl()

  const { addDomainsToBasket } = useDomainActionsDispatch()
  const domains = [
    {
      fqdn,
      plan: domain.plan_id,
    },
  ]

  const onBasketAddClick = useCallback(() => {
    if (isLoading) {
      return
    }

    if (isDomainInBasket) {
      onNextButtonClick?.()

      if (document.location.href.includes("bonus")) {
        navigate(url.getBasketUrl())

        return
      }

      document.location.href = forDomainButtons

      return
    }

    setAddCartSource(
      domains && domains[0].plan ? domains[0].plan : 0,
      eventSource || ""
    )

    addDomainsToBasket(domains, [], buttonId, false)
  }, [domains, buttonId, isLoading, isDomainInBasket])

  useEffect(() => {
    if (isDomainInBasket && !isEmpty(successButtonRef.current) && !isLoading) {
      successButtonRef.current?.focus()
    }
  }, [isDomainInBasket, successButtonRef, isLoading])

  if (isLoading || domain.plan_id === null) {
    return <LoaderAtm size="tiny" />
  }

  // AX drawer flow: if the domain is selected, show the next step button, otherwise show the add button
  if (onDomainSelected) {
    const isSelected = selectedFqdns?.includes(fqdn) ?? false

    if (isSelected) {
      return (
        <ButtonAtm
          title={_t("preBasket.nextStep")}
          color={hasOverriddenSuccessButtonColor ? color : "success"}
          icon={Tick}
          onClick={() => onNextButtonClick?.()}
          isExpanded={isSmallOnly}
          isWider={isMediumUp || isWider}
          size={isMediumUp ? "small" : "default"}
        >
          {_t("preBasket.nextStep")}
        </ButtonAtm>
      )
    }

    return (
      <ButtonAtm
        title={_t("domainSearch.button.add")}
        color={color}
        variant="outlined"
        isWider={isMediumUp || isWider}
        onClick={() => {
          if (!isEmpty(fqdn) && domain.plan_id !== null) {
            onDomainSelected(domain)
          }
        }}
        isExpanded={isSmallOnly ? isWider : undefined}
        size={isMediumUp ? "small" : "default"}
        dataTestId={buttonDataTestId}
      >
        {_t("domainSearch.button.add")}
      </ButtonAtm>
    )
  }

  if (isDomainInBasket) {
    return (
      <ButtonAtm
        ref={successButtonRef}
        title={_t("button.success")}
        color={hasOverriddenSuccessButtonColor ? color : "success"}
        variant="text"
        icon={Tick}
        onClick={onBasketAddClick}
        isExpanded={isSmallOnly}
        isWider={isMediumUp || isWider}
        size={isMediumUp ? "small" : "default"}
      >
        {_t("button.success")}
      </ButtonAtm>
    )
  }

  return (
    <ButtonAtm
      title={_t("domainSearch.button.addToCart")}
      color={color}
      variant={variant}
      icon={!isWider && hasIcon ? CartAdd : undefined}
      isWider={isMediumUp || isWider}
      onClick={onBasketAddClick}
      isExpanded={isSmallOnly ? isWider : undefined}
      size={isMediumUp ? "small" : "default"}
      dataTestId={buttonDataTestId}
    >
      {isMediumUp || isWider ? _t("domainSearch.button.addToCart") : null}
    </ButtonAtm>
  )
}
